import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BookOpen, CheckCircle2, AlertTriangle, Calendar, Route,
  Shield, Trophy, Award, ChevronRight, Play, Star, Target,
  Sparkles, Zap, Clock, TrendingUp, BarChart3, Megaphone
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { AdminLayout } from "../../components/layout/AdminLayout";

const LearnerDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDashboard(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDashboard = async () => {
    try {
      const [dashRes, recRes] = await Promise.all([
        api.get("/my/dashboard"),
        api.get("/my/recommendations").catch(() => ({ data: [] }))
      ]);
      setDashboard(dashRes.data);
      setRecommendations(recRes.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  if (loading) return <AdminLayout title="Dashboard" variant="learner"><div className="flex items-center justify-center py-24 text-muted-foreground">Loading dashboard...</div></AdminLayout>;
  if (!dashboard) return <AdminLayout title="Dashboard" variant="learner"><p>Failed to load dashboard</p></AdminLayout>;

  const totalEnrollments = dashboard.enrollments?.length || 0;
  const completionRate = totalEnrollments > 0 ? Math.round(dashboard.completed_courses / totalEnrollments * 100) : 0;

  return (
    <AdminLayout title="Dashboard" variant="learner">
      <div className="space-y-6" data-testid="learner-dashboard">
        {/* Welcome */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }} data-testid="learner-dashboard-title">
              Welcome back, {user?.full_name?.split(" ")[0] || "Learner"}
            </h1>
            <p className="text-muted-foreground text-sm">Here's your learning overview</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-medium">Level {dashboard.level}</p>
              <p className="text-xs text-muted-foreground">{dashboard.points} points</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-amber-500" />
            </div>
          </div>
        </div>

        {/* Quick Stats — Clickable */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: "In Progress", value: dashboard.in_progress_courses, icon: Play, color: "text-amber-500", bg: "bg-amber-500/10", link: "/my-courses" },
            { label: "Completed", value: dashboard.completed_courses, icon: CheckCircle2, color: "text-green-500", bg: "bg-green-500/10", link: "/my-progress" },
            { label: "Completion Rate", value: `${completionRate}%`, icon: TrendingUp, color: "text-primary", bg: "bg-primary/10", link: "/my-progress" },
            { label: "Badges", value: dashboard.badge_count, icon: Award, color: "text-purple-500", bg: "bg-purple-500/10", link: "/learner/gamification" },
            { label: "Points", value: dashboard.points, icon: Star, color: "text-amber-500", bg: "bg-amber-500/10", link: "/learner/gamification" },
          ].map((stat, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Card className="cursor-pointer hover:border-primary/20 transition-colors" onClick={() => navigate(stat.link)} data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center`}>
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* My Courses — with compliance tags */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base"><BookOpen className="w-5 h-5 text-primary" /> My Courses</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate("/my-courses")} data-testid="view-all-courses-btn">
                  View All <ChevronRight className="w-4 h-4 ml-0.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {dashboard.enrollments.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No courses enrolled</p>
              ) : (
                <div className="space-y-2">
                  {dashboard.enrollments.slice(0, 6).map((e, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => navigate(`/course/${e.course_id}`)}
                      data-testid={`enrollment-${i}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <h4 className="font-medium text-sm truncate">{e.course_title}</h4>
                          {e.is_compliance && (
                            <Badge className="bg-orange-500/10 text-orange-600 border-orange-500/20 text-[9px] shrink-0">Compliance</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Progress value={e.progress_percent || 0} className="h-1 flex-1 max-w-[100px]" />
                          <span className="text-xs text-muted-foreground">{e.progress_percent || 0}%</span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* My Learning Paths */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base"><Route className="w-5 h-5 text-primary" /> My Learning Paths</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate("/learner/learning-paths")} data-testid="view-all-paths-btn">
                  View All <ChevronRight className="w-4 h-4 ml-0.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {dashboard.learning_paths.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No learning paths assigned yet</p>
              ) : (
                <div className="space-y-3">
                  {dashboard.learning_paths.map((lp, i) => (
                    <div key={i} className="p-3 rounded-lg border hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => navigate("/learner/learning-paths")} data-testid={`lp-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-sm">{lp.path_title}</h4>
                        <Badge variant="outline" className="text-xs">{lp.status.replace("_"," ")}</Badge>
                      </div>
                      <Progress value={lp.progress} className="h-1.5 mb-1" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{lp.completed_courses}/{lp.total_courses} courses</span>
                        <span>{lp.progress}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* My Compliance */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base"><Shield className="w-5 h-5 text-primary" /> My Compliance</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate("/learner/compliance")} data-testid="view-all-compliance-btn">
                  View All <ChevronRight className="w-4 h-4 ml-0.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {dashboard.compliance.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No compliance items assigned</p>
              ) : (
                <div className="space-y-3">
                  {dashboard.compliance.map((c, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => navigate("/learner/compliance")} data-testid={`compliance-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-sm truncate">{c.title}</h4>
                          {c.is_mandatory && <Badge className="bg-red-500/10 text-red-600 border-red-500/20 text-[10px]">Required</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">Due: {c.due_date}</p>
                      </div>
                      <Badge className={`${c.status === "overdue" ? "bg-red-500/10 text-red-600" : c.status === "completed" ? "bg-green-500/10 text-green-600" : "bg-blue-500/10 text-blue-600"}`}>
                        {c.status === "overdue" && <AlertTriangle className="w-3 h-3 mr-1" />}
                        {c.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Sessions */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base"><Calendar className="w-5 h-5 text-primary" /> Upcoming Sessions</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate("/learner/sessions")} data-testid="view-all-sessions-btn">
                  View All <ChevronRight className="w-4 h-4 ml-0.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {dashboard.upcoming_sessions.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No upcoming sessions</p>
              ) : (
                <div className="space-y-3">
                  {dashboard.upcoming_sessions.map((s, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => navigate("/learner/sessions")} data-testid={`session-${i}`}>
                      <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                        <Calendar className="w-5 h-5 text-blue-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm">{s.title}</h4>
                        <p className="text-xs text-muted-foreground">{s.date} · {s.start_time}-{s.end_time} · {s.location}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">{s.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Announcements */}
        {dashboard.announcements?.length > 0 && (
          <Card data-testid="dashboard-announcements">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Megaphone className="w-5 h-5 text-primary" /> Announcements
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate("/learner/announcements")} data-testid="view-all-announcements-btn">
                  View All <ChevronRight className="w-4 h-4 ml-0.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboard.announcements.map((a, i) => (
                  <div key={a.id} className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => navigate("/learner/announcements")} data-testid={`announcement-${i}`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${a.priority === "high" ? "bg-red-500/10" : a.priority === "urgent" ? "bg-orange-500/10" : "bg-blue-500/10"}`}>
                      <Megaphone className={`w-4 h-4 ${a.priority === "high" ? "text-red-500" : a.priority === "urgent" ? "text-orange-500" : "text-blue-500"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-sm">{a.title}</h4>
                        {a.priority === "high" && <Badge className="bg-red-500/10 text-red-600 border-red-500/20 text-[9px]">Important</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{a.content}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {a.author_name} · {a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Links */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-medium text-muted-foreground">Quick Links:</span>
              {[
                { label: "Browse Catalog", icon: Sparkles, link: "/learner/catalog" },
                { label: "My Progress", icon: BarChart3, link: "/my-progress" },
                { label: "Certificates", icon: Award, link: "/learner/certificates" },
                { label: "Announcements", icon: Zap, link: "/learner/announcements" },
                { label: "Settings", icon: Target, link: "/learner/settings" },
              ].map((item) => (
                <Button key={item.label} variant="outline" size="sm" className="text-xs" onClick={() => navigate(item.link)} data-testid={`quick-link-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <item.icon className="w-3.5 h-3.5 mr-1" /> {item.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* AI Recommendations */}
        {recommendations.length > 0 && (
          <Card data-testid="recommendations-section">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Sparkles className="w-5 h-5 text-amber-500" /> Recommended for You
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                {recommendations.slice(0, 5).map((rec, i) => (
                  <motion.div
                    key={rec.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="p-3 rounded-xl border hover:border-primary/30 cursor-pointer transition-all hover:shadow-sm"
                    onClick={() => navigate(`/course/${rec.id}`)}
                    data-testid={`rec-course-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Zap className="w-4 h-4 text-primary" />
                      </div>
                      <Badge variant="outline" className="text-[10px]">{rec.course_type?.includes("scorm") ? "eLearning" : rec.course_type === "ilt" ? "ILT" : rec.course_type}</Badge>
                    </div>
                    <h4 className="font-medium text-sm line-clamp-2 mb-1">{rec.title}</h4>
                    <p className="text-[11px] text-primary/80 line-clamp-1">{rec.reasons?.[0]}</p>
                    {rec.duration_hours > 0 && <p className="text-[10px] text-muted-foreground mt-1">{rec.duration_hours}h</p>}
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default LearnerDashboardPage;
